import { createContext, useContext, useEffect, useState } from 'react';
import {
  User,
  onAuthStateChanged,
  signOut,
  signInWithPopup,
  GoogleAuthProvider,
} from 'firebase/auth';
import { auth } from './firebase';
import { authService } from '@/services/auth.service';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  loginWithGoogle: () => Promise<void>;
  logout: () => Promise<void>;
  error: string | null;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!auth) {
      setLoading(false);
      return;
    }

    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        // Set user immediately to avoid unnecessary delays
        setUser(firebaseUser);
        setError(null);
        setLoading(false);

        // Check if user exists in backend, only create if they don't
        try {
          const userExists = await authService.checkUserExists(firebaseUser.uid);
          if (!userExists) {
            await authService.createUser(firebaseUser);
          }
        } catch (err) {
          console.error('Error syncing user with backend:', err);
          // Don't set error - user can still use the app with Firebase auth
        }
      } else {
        setUser(null);
        setLoading(false);
      }
    });

    return unsubscribe;
  }, []);

  const loginWithGoogle = async () => {
    try {
      if (!auth) throw new Error('Firebase auth not initialized');
      setError(null);
      const provider = new GoogleAuthProvider();
      const result = await signInWithPopup(auth, provider);

      // User creation will be handled by onAuthStateChanged
      // But we can also call it here for immediate feedback
      try {
        await authService.createUser(result.user);
      } catch (err) {
        console.error('Error creating user in backend:', err);
        setError('Failed to sync user with backend');
      }
    } catch (err) {
      console.error('Error logging in:', err);
      setError('Failed to login with Google');
      throw err;
    }
  };

  const logout = async () => {
    try {
      if (!auth) throw new Error('Firebase auth not initialized');
      setError(null);
      await signOut(auth);
    } catch (err) {
      console.error('Error logging out:', err);
      setError('Failed to logout');
      throw err;
    }
  };

  return (
    <AuthContext.Provider value={{ user, loading, loginWithGoogle, logout, error }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
